/**
 * Raptor Predator Frontend - Graph Store
 * 
 * Manages the topology of the network graph (nodes, edges) WITHOUT positions.
 * Positions are managed in the Visual Graph (refs/buffers) for 60fps performance.
 * 
 * This follows the "Data Graph vs Visual Graph" separation pattern recommended
 * by peer review for scaling to 10K+ nodes.
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { 
  DeviceStatus, 
  LDCStatus,
  DeviceType,
  EdgeType,
} from '../types';

// ============================================================================
// Types
// ============================================================================

/** Lightweight node for graph topology (no position data) */
export interface TopologyNode {
  id: string;
  name: string;
  type: DeviceType;
  status: DeviceStatus;
  threatLevel: number; // 0-4 numeric for shader compatibility
  ldcId: string;
  ipAddress: string;
  lastSeen: number; // Unix timestamp for faster comparison
  fixed?: boolean; // For pinned nodes
  position?: { x: number; y: number; z: number }; // Initial position only
  metadata: {
    os?: string;
    agentVersion?: string;
    cpuUsage?: number;
    memoryUsage?: number;
  };
}

/** Lightweight edge for graph topology */
export interface TopologyEdge {
  id: string;
  sourceId: string; // Renamed from 'source' for consistency
  targetId: string; // Renamed from 'target' for consistency
  type: EdgeType;
  threatLevel: number; // 0-4 numeric for shader compatibility
  weight: number;
}

/** Adjacency list for fast neighbor lookups */
export type AdjacencyMap = Map<string, Set<string>>;

/** Graph statistics */
export interface GraphStats {
  totalNodes: number;
  onlineNodes: number;
  offlineNodes: number;
  warningNodes: number;
  criticalNodes: number;
  totalEdges: number;
  nodesByType: Record<DeviceType, number>;
  nodesByLdc: Record<string, number>;
}

/** Graph store state */
interface GraphState {
  // Topology data (no positions!)
  nodes: Map<string, TopologyNode>;
  edges: Map<string, TopologyEdge>;
  adjacency: AdjacencyMap;
  reverseAdjacency: AdjacencyMap; // For incoming edges
  
  // LDC data
  ldcs: Map<string, LDCStatus>;
  
  // Computed stats (cached)
  stats: GraphStats;
  
  // Selection state
  selectedNodeIds: Set<string>;
  hoveredNodeId: string | null;
  highlightedNodeIds: Set<string>;
  
  // Versioning for change detection
  version: number;
  lastUpdate: number;
  
  // Loading state
  isLoading: boolean;
  error: string | null;
}

/** Graph store actions */
interface GraphActions {
  // Node operations
  addNode: (node: TopologyNode) => void;
  addNodes: (nodes: TopologyNode[]) => void;
  updateNode: (id: string, updates: Partial<TopologyNode>) => void;
  removeNode: (id: string) => void;
  
  // Edge operations
  addEdge: (edge: TopologyEdge) => void;
  addEdges: (edges: TopologyEdge[]) => void;
  removeEdge: (id: string) => void;
  
  // LDC operations
  setLdcs: (ldcs: LDCStatus[]) => void;
  updateLdc: (id: string, updates: Partial<LDCStatus>) => void;
  
  // Bulk operations
  setGraph: (nodes: TopologyNode[], edges: TopologyEdge[]) => void;
  clearGraph: () => void;
  
  // Query helpers
  getNodesByLdc: (ldcId: string) => TopologyNode[];
  getNodesByStatus: (status: DeviceStatus) => TopologyNode[];
  getNodesByThreatLevel: (level: number) => TopologyNode[];
  getNeighbors: (nodeId: string) => string[];
  getIncomingNeighbors: (nodeId: string) => string[];
  
  // Selection operations
  selectNode: (nodeId: string | null) => void;
  toggleNodeSelection: (nodeId: string) => void;
  clearSelection: () => void;
  setHoveredNode: (nodeId: string | null) => void;
  
  // Highlight operations (for attack path visualization)
  highlightNodes: (nodeIds: string[]) => void;
  clearHighlights: () => void;
  
  // State management
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialStats: GraphStats = {
  totalNodes: 0,
  onlineNodes: 0,
  offlineNodes: 0,
  warningNodes: 0,
  criticalNodes: 0,
  totalEdges: 0,
  nodesByType: {} as Record<DeviceType, number>,
  nodesByLdc: {},
};

const initialState: GraphState = {
  nodes: new Map(),
  edges: new Map(),
  adjacency: new Map(),
  reverseAdjacency: new Map(),
  ldcs: new Map(),
  stats: initialStats,
  selectedNodeIds: new Set(),
  hoveredNodeId: null,
  highlightedNodeIds: new Set(),
  version: 0,
  lastUpdate: Date.now(),
  isLoading: false,
  error: null,
};

// ============================================================================
// Helper Functions
// ============================================================================

/** Recompute graph statistics */
function computeStats(nodes: Map<string, TopologyNode>, edges: Map<string, TopologyEdge>): GraphStats {
  const stats: GraphStats = {
    totalNodes: nodes.size,
    onlineNodes: 0,
    offlineNodes: 0,
    warningNodes: 0,
    criticalNodes: 0,
    totalEdges: edges.size,
    nodesByType: {} as Record<DeviceType, number>,
    nodesByLdc: {},
  };
  
  for (const node of nodes.values()) {
    // Count by status
    switch (node.status) {
      case 'online':
        stats.onlineNodes++;
        break;
      case 'offline':
        stats.offlineNodes++;
        break;
      case 'warning':
        stats.warningNodes++;
        break;
      case 'critical':
        stats.criticalNodes++;
        break;
    }
    
    // Count by type
    stats.nodesByType[node.type] = (stats.nodesByType[node.type] || 0) + 1;
    
    // Count by LDC
    stats.nodesByLdc[node.ldcId] = (stats.nodesByLdc[node.ldcId] || 0) + 1;
  }
  
  return stats;
}

/** Add edge to adjacency lists */
function addToAdjacency(
  adjacency: AdjacencyMap,
  reverseAdjacency: AdjacencyMap,
  edge: TopologyEdge
): void {
  // Forward adjacency
  if (!adjacency.has(edge.sourceId)) {
    adjacency.set(edge.sourceId, new Set());
  }
  adjacency.get(edge.sourceId)!.add(edge.targetId);
  
  // Reverse adjacency
  if (!reverseAdjacency.has(edge.targetId)) {
    reverseAdjacency.set(edge.targetId, new Set());
  }
  reverseAdjacency.get(edge.targetId)!.add(edge.sourceId);
}

/** Remove edge from adjacency lists */
function removeFromAdjacency(
  adjacency: AdjacencyMap,
  reverseAdjacency: AdjacencyMap,
  edge: TopologyEdge
): void {
  adjacency.get(edge.sourceId)?.delete(edge.targetId);
  reverseAdjacency.get(edge.targetId)?.delete(edge.sourceId);
}

// ============================================================================
// Store Creation
// ============================================================================

export const useGraphStore = create<GraphState & GraphActions>()(
  devtools(
    subscribeWithSelector(
      immer((set, get) => ({
        ...initialState,
        
        // Node operations
        addNode: (node) => { set((state) => {
          state.nodes.set(node.id, node);
          state.stats = computeStats(state.nodes, state.edges);
          state.version++;
          state.lastUpdate = Date.now();
        }); },
        
        addNodes: (nodes) => { set((state) => {
          for (const node of nodes) {
            state.nodes.set(node.id, node);
          }
          state.stats = computeStats(state.nodes, state.edges);
          state.version++;
          state.lastUpdate = Date.now();
        }); },
        
        updateNode: (id, updates) => { set((state) => {
          const node = state.nodes.get(id);
          if (node) {
            Object.assign(node, updates);
            state.stats = computeStats(state.nodes, state.edges);
            state.version++;
            state.lastUpdate = Date.now();
          }
        }); },
        
        removeNode: (id) => { set((state) => {
          state.nodes.delete(id);
          
          // Remove associated edges
          const edgesToRemove: string[] = [];
          for (const [edgeId, edge] of state.edges) {
            if (edge.sourceId === id || edge.targetId === id) {
              edgesToRemove.push(edgeId);
              removeFromAdjacency(state.adjacency, state.reverseAdjacency, edge);
            }
          }
          for (const edgeId of edgesToRemove) {
            state.edges.delete(edgeId);
          }
          
          // Clean adjacency
          state.adjacency.delete(id);
          state.reverseAdjacency.delete(id);
          
          state.stats = computeStats(state.nodes, state.edges);
          state.version++;
          state.lastUpdate = Date.now();
        }); },
        
        // Edge operations
        addEdge: (edge) => { set((state) => {
          state.edges.set(edge.id, edge);
          addToAdjacency(state.adjacency, state.reverseAdjacency, edge);
          state.stats = computeStats(state.nodes, state.edges);
          state.version++;
          state.lastUpdate = Date.now();
        }); },
        
        addEdges: (edges) => { set((state) => {
          for (const edge of edges) {
            state.edges.set(edge.id, edge);
            addToAdjacency(state.adjacency, state.reverseAdjacency, edge);
          }
          state.stats = computeStats(state.nodes, state.edges);
          state.version++;
          state.lastUpdate = Date.now();
        }); },
        
        removeEdge: (id) => { set((state) => {
          const edge = state.edges.get(id);
          if (edge) {
            removeFromAdjacency(state.adjacency, state.reverseAdjacency, edge);
            state.edges.delete(id);
            state.stats = computeStats(state.nodes, state.edges);
            state.version++;
            state.lastUpdate = Date.now();
          }
        }); },
        
        // LDC operations
        setLdcs: (ldcs) => { set((state) => {
          state.ldcs.clear();
          for (const ldc of ldcs) {
            state.ldcs.set(ldc.id, ldc);
          }
          state.version++;
          state.lastUpdate = Date.now();
        }); },
        
        updateLdc: (id, updates) => { set((state) => {
          const ldc = state.ldcs.get(id);
          if (ldc) {
            Object.assign(ldc, updates);
            state.version++;
            state.lastUpdate = Date.now();
          }
        }); },
        
        // Bulk operations
        setGraph: (nodes, edges) => { set((state) => {
          state.nodes.clear();
          state.edges.clear();
          state.adjacency.clear();
          state.reverseAdjacency.clear();
          
          for (const node of nodes) {
            state.nodes.set(node.id, node);
          }
          
          for (const edge of edges) {
            state.edges.set(edge.id, edge);
            addToAdjacency(state.adjacency, state.reverseAdjacency, edge);
          }
          
          state.stats = computeStats(state.nodes, state.edges);
          state.version++;
          state.lastUpdate = Date.now();
        }); },
        
        clearGraph: () => { set((state) => {
          state.nodes.clear();
          state.edges.clear();
          state.adjacency.clear();
          state.reverseAdjacency.clear();
          state.stats = initialStats;
          state.version++;
          state.lastUpdate = Date.now();
        }); },
        
        // Query helpers (not Immer - return computed values)
        getNodesByLdc: (ldcId) => {
          const result: TopologyNode[] = [];
          for (const node of get().nodes.values()) {
            if (node.ldcId === ldcId) {
              result.push(node);
            }
          }
          return result;
        },
        
        getNodesByStatus: (status) => {
          const result: TopologyNode[] = [];
          for (const node of get().nodes.values()) {
            if (node.status === status) {
              result.push(node);
            }
          }
          return result;
        },
        
        getNodesByThreatLevel: (level) => {
          const result: TopologyNode[] = [];
          for (const node of get().nodes.values()) {
            if (node.threatLevel === level) {
              result.push(node);
            }
          }
          return result;
        },
        
        getNeighbors: (nodeId) => {
          const neighbors = get().adjacency.get(nodeId);
          return neighbors ? Array.from(neighbors) : [];
        },
        
        getIncomingNeighbors: (nodeId) => {
          const neighbors = get().reverseAdjacency.get(nodeId);
          return neighbors ? Array.from(neighbors) : [];
        },
        
        // Selection operations
        selectNode: (nodeId) => { set((state) => {
          state.selectedNodeIds.clear();
          if (nodeId) {
            state.selectedNodeIds.add(nodeId);
          }
        }); },
        
        toggleNodeSelection: (nodeId) => { set((state) => {
          if (state.selectedNodeIds.has(nodeId)) {
            state.selectedNodeIds.delete(nodeId);
          } else {
            state.selectedNodeIds.add(nodeId);
          }
        }); },
        
        clearSelection: () => { set((state) => {
          state.selectedNodeIds.clear();
        }); },
        
        setHoveredNode: (nodeId) => { set((state) => {
          state.hoveredNodeId = nodeId;
        }); },
        
        // Highlight operations (for attack path visualization)
        highlightNodes: (nodeIds) => { set((state) => {
          state.highlightedNodeIds.clear();
          for (const id of nodeIds) {
            state.highlightedNodeIds.add(id);
          }
        }); },
        
        clearHighlights: () => { set((state) => {
          state.highlightedNodeIds.clear();
        }); },
        
        // State management
        setLoading: (loading) => { set((state) => {
          state.isLoading = loading;
        }); },
        
        setError: (error) => { set((state) => {
          state.error = error;
          state.isLoading = false;
        }); },
      }))
    ),
    { name: 'graph-store' }
  )
);

// ============================================================================
// Selectors (for granular subscriptions)
// ============================================================================

export const selectNodes = (state: GraphState & GraphActions) => state.nodes;
export const selectEdges = (state: GraphState & GraphActions) => state.edges;
export const selectStats = (state: GraphState & GraphActions) => state.stats;
export const selectNodeCount = (state: GraphState & GraphActions) => state.nodes.size;
export const selectEdgeCount = (state: GraphState & GraphActions) => state.edges.size;
export const selectVersion = (state: GraphState & GraphActions) => state.version;
export const selectIsLoading = (state: GraphState & GraphActions) => state.isLoading;
export const selectError = (state: GraphState & GraphActions) => state.error;
export const selectLdcs = (state: GraphState & GraphActions) => state.ldcs;
export const selectSelectedNodeIds = (state: GraphState & GraphActions) => state.selectedNodeIds;
export const selectHoveredNodeId = (state: GraphState & GraphActions) => state.hoveredNodeId;
